(() => {
  const TOPPING_ORDER = [
    'Olives',
    'Bell Pepper',
    'Ham (Pork)',
    'Oregano'
  ];

  function normalize(value) {
    return String(value || '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function parseCombination(value) {
    const normalized = normalize(value);

    if (!normalized || normalized.toLowerCase() === 'none') {
      return [];
    }

    return normalized
      .split(/\s*\+\s*/)
      .map(normalize)
      .filter(Boolean);
  }

  function buildCombination(selectedToppings) {
    const ordered = TOPPING_ORDER.filter(topping =>
      selectedToppings.includes(topping)
    );

    return ordered.length
      ? ordered.join(' + ')
      : 'None';
  }

  function getNativeSelect(picker) {
    return picker.querySelector(
      '[data-toppings-native-select]'
    );
  }

  function getButtons(picker) {
    return Array.from(
      picker.querySelectorAll('[data-topping]')
    );
  }

  function syncButtonsFromSelect(picker) {
    const nativeSelect = getNativeSelect(picker);
    const buttons = getButtons(picker);

    if (!nativeSelect || !buttons.length) {
      return;
    }

    const selectedToppings = parseCombination(
      nativeSelect.value
    );

    buttons.forEach(button => {
      const topping = normalize(
        button.dataset.topping
      );

      const selected = selectedToppings.includes(
        topping
      );

      button.classList.toggle(
        'is-selected',
        selected
      );

      button.setAttribute(
        'aria-pressed',
        selected ? 'true' : 'false'
      );
    });
  }

  function getSelectedToppings(picker) {
    return getButtons(picker)
      .filter(button =>
        button.classList.contains('is-selected')
      )
      .map(button =>
        normalize(button.dataset.topping)
      );
  }

  function selectCombination(picker) {
    const nativeSelect = getNativeSelect(picker);

    if (!nativeSelect) {
      return;
    }

    const selectedToppings =
      getSelectedToppings(picker);

    const combination =
      buildCombination(selectedToppings);

    const matchingOption = Array.from(
      nativeSelect.options
    ).find(option =>
      normalize(option.value) === normalize(combination)
    );

    if (!matchingOption) {
      console.error(
        '[Clement Toppings] Missing Shopify option:',
        combination,
        'Available options:',
        Array.from(nativeSelect.options).map(
          option => option.value
        )
      );

      syncButtonsFromSelect(picker);
      return;
    }

    /*
      Set the real Shopify option value.
    */
    nativeSelect.value = matchingOption.value;

    /*
      Keep option state explicit.
    */
    Array.from(nativeSelect.options).forEach(option => {
      option.selected = option === matchingOption;
    });

    /*
      Trigger the theme's existing ProductVariants system.
    */
    nativeSelect.dispatchEvent(
      new Event('change', {
        bubbles: true
      })
    );
  }

  function initializeProductVariants(productVariants) {
    if (!productVariants) {
      return;
    }

    const picker = productVariants.querySelector(
      '[data-toppings-picker]'
    );

    if (!picker) {
      return;
    }

    /*
      Avoid attaching duplicate VARIANT_CHANGE listeners.
    */
    if (!productVariants.dataset.clementToppingsInitialized) {
      productVariants.dataset.clementToppingsInitialized = 'true';

      productVariants.addEventListener(
        'VARIANT_CHANGE',
        () => {
          requestAnimationFrame(() => {
            const currentPicker =
              productVariants.querySelector(
                '[data-toppings-picker]'
              );

            if (currentPicker) {
              syncButtonsFromSelect(currentPicker);
            }
          });
        }
      );
    }

    syncButtonsFromSelect(picker);
  }

  function initializeAll(root = document) {
    root
      .querySelectorAll('product-variants')
      .forEach(initializeProductVariants);
  }

  /*
    One delegated click handler.
    This also works when Shopify/theme replaces
    variant markup dynamically.
  */
  document.addEventListener('click', event => {
    const button = event.target.closest(
      '[data-toppings-picker] [data-topping]'
    );

    if (!button) {
      return;
    }

    event.preventDefault();

    const picker = button.closest(
      '[data-toppings-picker]'
    );

    if (!picker) {
      return;
    }

    const nextSelected =
      !button.classList.contains('is-selected');

    button.classList.toggle(
      'is-selected',
      nextSelected
    );

    button.setAttribute(
      'aria-pressed',
      nextSelected ? 'true' : 'false'
    );

    selectCombination(picker);
  });

  /*
    Initial page load.
  */
  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initializeAll()
    );
  } else {
    initializeAll();
  }

  /*
    Shopify theme editor section reloads.
  */
  document.addEventListener(
    'shopify:section:load',
    event => {
      initializeAll(event.target);
    }
  );

  /*
    Catch dynamically inserted quick views/helper markup.
  */
  const observer = new MutationObserver(mutations => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) {
          continue;
        }

        if (node.matches?.('product-variants')) {
          initializeProductVariants(node);
        }

        node
          .querySelectorAll?.('product-variants')
          .forEach(initializeProductVariants);
      }
    }
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();